// @ts-nocheck
import { useState } from "react";
import { canManagePortalUsers } from "@/services/jambushrustiAuth";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  BookPlus,
  BookCheck,
  ScrollText,
  Users,
  CheckSquare,
  BarChart3,
  Ticket,
  Settings,
  Warehouse,
  ChevronLeft,
  Search,
  Menu,
  X,
  Sparkles,
  Library,
  IndianRupee,
} from "lucide-react";
import { cn } from "@libraryadmin/lib/utils";
import { Input } from "@libraryadmin/components/ui/input";
import { Button } from "@libraryadmin/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { getDashboardStats } from "@libraryadmin/services/api";
import { getLibraryAdminSession } from "@libraryadmin/services/auth";

const menuItems = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/library-admin",
    category: "main",
  },
  {
    icon: BookPlus,
    label: "Book Entry",
    path: "/library-admin/book-entry",
    category: "catalog",
  },
  {
    icon: BookOpen,
    label: "Book Issue",
    path: "/library-admin/book-issue",
    category: "catalog",
  },
  {
    icon: BookCheck,
    label: "Book Receive",
    path: "/library-admin/book-receive",
    category: "catalog",
  },
  {
    icon: Warehouse,
    label: "Storage",
    path: "/library-admin/storage",
    category: "catalog",
  },
  {
    icon: ScrollText,
    label: "Manuscripts",
    path: "/library-admin/manuscripts",
    category: "jain",
  },
  {
    icon: Users,
    label: "Members",
    path: "/library-admin/members",
    category: "management",
  },
  {
    icon: Users,
    label: "Roles & Permissions",
    path: "/library-admin/users",
    category: "management",
  },

  {
    icon: IndianRupee,
    label: "Payment",
    path: "/library-admin/fine-management",
    category: "management",
  },
  // {
  //   icon: CheckSquare,
  //   label: "Approvals",
  //   path: "/library-admin/approvals",
  //   category: "management",
  // },
  {
    icon: BarChart3,
    label: "Reports",
    path: "/library-admin/reports",
    category: "analytics",
  },
  // {
  //   icon: Ticket,
  //   label: "Tickets",
  //   path: "/library-admin/tickets",
  //   category: "support",
  // },
  // {
  //   icon: Settings,
  //   label: "Settings",
  //   path: "/library-admin/settings",
  //   category: "system",
  // },
];

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

export function Sidebar({
  collapsed,
  setCollapsed,
  mobileOpen,
  setMobileOpen,
}: SidebarProps) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: getDashboardStats,
    staleTime: 60_000,
  });

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setMobileOpen(false);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-sidebar-border shadow-xl transition-all duration-300",
          collapsed ? "w-[72px]" : "w-72",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        style={{
          background: "var(--gradient-sidebar)",
        }}
      >
        {/* Decorative overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23224D9F' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Logo Section */}
        <div className="relative flex items-center gap-3 border-b border-sidebar-border px-4 py-5">
          <div
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl shadow-md ring-2 ring-primary/20"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Library className="h-6 w-6 text-white" />
          </div>
          {!collapsed && (
            <div className="animate-fade-in">
              <h1 className="text-base font-bold tracking-wide text-sidebar-foreground">
                Jambushrushti
              </h1>
              <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-sidebar-foreground/70">
                <Sparkles className="h-3 w-3 text-accent" />
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text font-semibold text-transparent">
                  Jain Library
                </span>
              </p>
            </div>
          )}
          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto text-sidebar-foreground hover:bg-sidebar-accent lg:hidden"
            onClick={() => setMobileOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        {!collapsed && (
          <div className="px-4 py-4">
            <div className="group relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-foreground/40 transition-colors group-focus-within:text-accent" />
              <Input
                placeholder="Search library..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="shadow-xs h-11 rounded-xl border-sidebar-border bg-background/80 pl-9 text-sidebar-foreground transition-all duration-300 placeholder:text-sidebar-foreground/40 focus:border-accent/50 focus:bg-background focus:ring-1 focus:ring-accent/30"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="relative flex-1 overflow-y-auto px-3 py-2">
          {/* Quick Access Label */}
          {!collapsed && (
            <div className="mb-2 px-3 py-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-sidebar-foreground/50">
                Navigation
              </span>
            </div>
          )}

          <ul className="space-y-1">
            {(getLibraryAdminSession()?.can_access_library
              ? menuItems.filter(
                  item =>
                    (item.path !== "/library-admin/users" ||
                      canManagePortalUsers(getLibraryAdminSession())) &&
                    item.label
                      .toLowerCase()
                      .includes(searchQuery.trim().toLowerCase())
                )
              : []
            ).map((item, index) => {
              const isActive =
                item.path === "/library-admin"
                  ? location.pathname === item.path
                  : location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);
              return (
                <li
                  key={item.path}
                  className="animate-slide-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                >
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={cn(
                      "sidebar-item group",
                      isActive && "sidebar-item-active",
                      collapsed && "justify-center px-2"
                    )}
                    title={collapsed ? item.label : undefined}
                  >
                    <item.icon
                      className={cn(
                        "h-[18px] w-[18px] flex-shrink-0 transition-all duration-300 group-hover:scale-110",
                        isActive
                          ? "text-accent-foreground"
                          : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground"
                      )}
                    />
                    {!collapsed && (
                      <span
                        className={cn(
                          "text-[13px] font-medium transition-colors",
                          isActive
                            ? "font-semibold text-accent-foreground"
                            : "text-sidebar-foreground/80 group-hover:text-sidebar-foreground"
                        )}
                      >
                        {item.label}
                      </span>
                    )}
                    {!collapsed && item.category === "jain" && (
                      <span className="ml-auto rounded-full border border-accent/20 bg-accent/15 px-2 py-0.5 text-[9px] font-bold tracking-wide text-accent">
                        Jain
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Section */}
        <div className="relative border-t border-sidebar-border px-3 py-4">
          {!collapsed && (
            <div className="shadow-xs mb-3 flex items-center gap-3 rounded-xl border border-accent/20 bg-accent/10 px-3 py-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/20">
                <Library className="h-4 w-4 text-accent" />
              </div>
              <div className="text-[12px] text-sidebar-foreground/80">
                <span className="text-sm font-bold text-sidebar-foreground">
                  {stats ? stats.totalBooks.toLocaleString() : "—"}
                </span>
                <span className="block text-[10px] text-sidebar-foreground/60">
                  Books in Library
                </span>
              </div>
            </div>
          )}

          {/* Collapse Button - Desktop Only */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="sidebar-item hidden w-full hover:bg-sidebar-accent lg:flex"
          >
            <ChevronLeft
              className={cn(
                "h-4 w-4 text-sidebar-foreground/70 transition-transform duration-300",
                collapsed && "rotate-180"
              )}
            />
            {!collapsed && (
              <span className="text-[13px] font-medium text-sidebar-foreground/80">
                Collapse
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="hover:bg-muted lg:hidden"
      onClick={onClick}
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}
